import { IColorMix } from "@/src/llm/Annotations"; /* Been Here */
import { genModelViewMatrices, ICamera, ICameraPos, updateCamera } from "@/src/llm/Camera"; /* Been Here */
import { SavedState } from "@/src/llm/SavedState"; /* Been Here */

import { drawAllArrows } from "@/src/llm/components/Arrow"; /* Been Here */
import { drawBlockInfo } from "@/src/llm/components/BlockInfo"; /* Been Here */
import { drawModelCard } from "@/src/llm/components/ModelCard"; /* Been Here */
import { IMovementInfo } from "@/src/llm/components/MovementControls"; /* Been Here */
import { drawBlockLabels } from "@/src/llm/components/SectionLabels"; /* Been Here */

import { IGptModelLink, IGpuGptModel, IModelShape } from "@/src/llm/GptModel"; /* Been Here */
import { genGptModelLayout, IBlkDef, IGptModelLayout } from "@/src/llm/GptModelLayout"; /* Been Here */
import { IWasmGptModel, stepWasmModel, syncWasmDataWithJsAndGpu } from "@/src/llm/GptModelWasm"; /* Been Here */
import { runMouseHitTesting } from "@/src/llm/Interaction"; /* Been Here */
import { NativeFunctions } from "@/src/llm/NativeBindings"; /* Been Here */

import { IBlockRender, initBlockRender } from "@/src/llm/render/blockRender"; /* Been Here */
import { drawText, IFontAtlasData, IFontOpts, measureText } from "@/src/llm/render/fontRender"; /* Been Here */
import { initRender, IRenderState, IRenderView, renderModel, resetRenderBuffers } from "@/src/llm/render/modelRender"; /* Been Here */
import { beginQueryAndGetPrevMs, endQuery } from "@/src/llm/render/queryManager"; /* Been Here */
import { RenderPhase } from "@/src/llm/render/sharedRender"; /* Been Here */

import { isNotNil } from "@/src/utils/data"; /* Been Here */
import { Subscriptions } from "@/src/utils/hooks"; /* Been Here */
import { ILayout } from "@/src/utils/layout"; /* Been Here */
import { Mat4f } from "@/src/utils/matrix"; /* Been Here */
import { Vec3, Vec4 } from "@/src/utils/vector"; /* Been Here */

import { initWalkthrough, runWalkthrough } from "@/src/llm/walkthrough/Walkthrough"; /* Been Here */
import { DimStyle } from "@/src/llm/walkthrough/WalkthroughTools"; /* Been Here */


export interface IProgramState {
  native: NativeFunctions | null;
  wasmGptModel: IWasmGptModel | null;
  stepModel: boolean;
  mouse: IMouseState;
  render: IRenderState;
  inWalkthrough: boolean;
  walkthrough: ReturnType<typeof initWalkthrough>;
  camera: ICamera;
  htmlSubs: Subscriptions;
  layout: IGptModelLayout;
  mainExample: IModelExample;
  examples: IModelExample[];
  currExampleId: number;
  shape: IModelShape;
  gptGpuModel: IGpuGptModel | null;
  jsGptModel: IGptModelLink | null;
  movement: IMovementInfo;
  display: IDisplayState;
  pageLayout: ILayout;
  markDirty: () => void;
}


export interface IModelExample {
  name: string;
  shape: IModelShape;
  enabled: boolean;
  layout?: IGptModelLayout;
  blockRender: IBlockRender;
  offset: Vec3;
  modelCardOffset: Vec3;
  camera?: ICameraPos;
}


export interface IMouseState {
  mousePos: Vec3;
}


export interface IDisplayState {
  tokenColors: IColorMix | null;
  tokenIdxColors: IColorMix | null;
  tokenOutputColors: IColorMix | null;
  tokenIdxModelOpacity?: number[];
  topOutputOpacity?: number;
  lines: string[];
  hoverTarget: IHoverTarget | null;
  blkIdxHover: number[] | null;
  dimHover: DimStyle | null;
}


export interface IHoverTarget {
  subCube: IBlkDef;
  mainCube: IBlkDef;
  mainIdx: Vec3;
}


export function initProgramState(canvasEl: HTMLCanvasElement, fontAtlasData: IFontAtlasData): IProgramState {
  let render = initRender(canvasEl, fontAtlasData);
  let walkthrough = initWalkthrough();

  let prevState = SavedState.state;
  let camera: ICamera = {
    angle: prevState?.camera.angle ?? new Vec3(296, 16, 13.5),
    center: prevState?.camera.center ?? new Vec3(-8.4, 0, -481.5),
    transition: {},
    modelMtx: new Mat4f(),
    viewMtx: new Mat4f(),
    lookAtMtx: new Mat4f(),
    camPos: new Vec3(),
    camPosModel: new Vec3(),
  }

  let shape: IModelShape = {
    B: 1,
    T: 11,
    C: 48,
    nHeads: 3,
    A: 48 / 3,
    nBlocks: 3,
    vocabSize: 3,
  };

  let gpt2ShapeSmall: IModelShape = {
    B: 1,
    T: 1024,
    C: 768,
    nHeads: 12,
    A: 768 / 12,
    nBlocks: 12,
    vocabSize: 50257,
  };

  let gpt2ShapeLarge: IModelShape = {
    B: 1,
    T: 1024,
    C: 1600,
    nHeads: 25,
    A: 1600 / 25,
    nBlocks: 48,
    vocabSize: 50257,
  };

  let gpt3Shape: IModelShape = {
    B: 1,
    T: 1024,
    C: 12288,
    nHeads: 96,
    A: 12288 / 96,
    nBlocks: 96,
    vocabSize: 50257,
  };

  function makeCamera(center: Vec3, angle: Vec3): ICameraPos {
    return { center, angle };
  }

  let delta = new Vec3(10000, 0, 0);

  return {
    native: null,
    wasmGptModel: null,
    render: render!,
    inWalkthrough: true,
    walkthrough,
    camera,
    shape: shape,
    layout: genGptModelLayout(shape),
    currExampleId: -1,
    mainExample: {
      name: 'nano-gpt',
      enabled: true,
      shape: shape,
      offset: new Vec3(),
      modelCardOffset: new Vec3(),
      blockRender: null!,
      camera: makeCamera(new Vec3(42.771, 0.000, -569.287), new Vec3(284.959, 26.501, 12.867)),
    },
    examples: [{
      name: 'GPT-2 (small)',
      enabled: true,
      shape: gpt2ShapeSmall,
      offset: delta.mul(-5),
      modelCardOffset: delta.mul(-2.0),
      blockRender: initBlockRender(render?.ctx ?? null),
      camera: makeCamera(new Vec3(-65141.321, 0.000, -69843.439), new Vec3(224.459, 24.501, 1574.240)),
    }, {
      name: 'GPT-2 (XL)',
      enabled: true,
      shape: gpt2ShapeLarge,
      offset: delta.mul(20),
      modelCardOffset: delta.mul(0.5),
      blockRender: initBlockRender(render?.ctx ?? null),
      camera: makeCamera(new Vec3(237902.688, 0.000, -47282.484), new Vec3(311.959, 23.501, 1382.449)),
    }, {
      name: 'GPT-3',
      enabled: false,
      shape: gpt3Shape,
      offset: delta.mul(50.0),
      modelCardOffset: delta.mul(15.0),
      blockRender: initBlockRender(render?.ctx ?? null),
      camera: makeCamera(new Vec3(837678.163, 0.000, -485242.286), new Vec3(238.959, 10.501, 12583.939)),
    }],
    gptGpuModel: null,
    jsGptModel: null,
    stepModel: false,
    markDirty: () => { },
    htmlSubs: new Subscriptions(),
    mouse: {
      mousePos: new Vec3(),
    },
    movement: {
      action: null,
      actionHover: null,
      target: [0, 0],
      depth: 1,
      cameraLerp: null,
    },
    display: {
      tokenColors: null,
      tokenIdxColors: null,
      tokenOutputColors: null,
      lines: [],
      hoverTarget: null,
      dimHover: null,
      blkIdxHover: null,
    },
    pageLayout: {
      height: 0,
      width: 0,
      isDesktop: true,
      isPhone: true,
    }
  };
}


export function runProgram(view: IRenderView, state: IProgramState) {
  let timer0 = performance.now();

  if (!state.render) {
    return;
  }

  resetRenderBuffers(state.render);
  state.render.sharedRender.activePhase = RenderPhase.Opaque;
  state.display.lines = [];
  state.display.hoverTarget = null;
  state.display.tokenColors = null;
  state.display.tokenIdxColors = null;

  if (state.wasmGptModel && state.jsGptModel) {
    syncWasmDataWithJsAndGpu(state.wasmGptModel, state.jsGptModel);
  }

  if (state.stepModel && state.wasmGptModel && state.jsGptModel) {
    state.stepModel = false;
    stepWasmModel(state.wasmGptModel, state.jsGptModel);
  }

  // generate the base model, incorporating the gpu-side model if available
  state.layout = genGptModelLayout(state.shape, state.jsGptModel);

  // @TODO: handle different models in the same scene.
  // Maybe need to copy a lot of different things like the entire render state per model?
  for (let example of state.examples) {
    if (example.enabled && !example.layout) {
      let layout = genGptModelLayout(example.shape, null, example.offset);
      example.layout = layout;
    }
  }

  genModelViewMatrices(state, state.layout!);

  let queryRes = beginQueryAndGetPrevMs(state.render.queryManager, 'render');
  if (isNotNil(queryRes)) {
    state.render.lastGpuMs = queryRes;
  }

  state.render.renderTiming = false; // state.pageLayout.isDesktop;

  // will modify layout; view; render a few things.
  if (state.inWalkthrough) {
    runWalkthrough(state, view);
  }

  updateCamera(state, view);

  drawBlockInfo(state);
  // these will get modified by the walkthrough (stored where?)
  drawAllArrows(state.render, state.layout);

  drawModelCard(state, state.layout, 'nano-gpt', new Vec3());
  // drawTokens(state.render, state.layout, state.display);

  for (let example of state.examples) {
    if (example.enabled && example.layout) {
      drawModelCard(state, example.layout, example.name, example.offset.add(example.modelCardOffset));
    }
  }

  runMouseHitTesting(state);
  state.render.sharedRender.activePhase = RenderPhase.Opaque;
  drawBlockLabels(state.render, state.layout);

  let lineNo = 1;
  let tw = state.render.size.x;
  state.render.sharedRender.activePhase = RenderPhase.Overlay2D;
  for (let line of state.display.lines) {
    let opts: IFontOpts = { color: new Vec4(), size: 14 };
    let w = measureText(state.render.modelFontBuf, line, opts);
    drawText(state.render.modelFontBuf, line, tw - w - 4, lineNo * opts.size * 1.3 + 4, opts)

    lineNo++;
  }

  // render everything; i.e. here's where we actually do gl draw calls
  // up until now, we've just been putting data in cpu-side buffers
  renderModel(state);

  endQuery(state.render.queryManager, 'render');
  state.render.gl.flush();

  state.render.lastJsMs = performance.now() - timer0;
}

/* Observed */